import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { getStoreFollowStatus, followStore, unfollowStore } from "../../api/storeService";
import { getStoreCoupons } from "../../api/couponService";
import { startStoreConversation } from "../../api/chatService";
import Skeleton from "./Skeleton";
import "./clientTheme.css";
import "./StoreHeader.css";

function VerifiedIcon() {
  const { t } = useTranslation("storeHeader");
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" className="store-header-verified-icon" aria-label={t("verifiedStore")}>
      <circle cx="12" cy="12" r="12" fill="currentColor" />
      <path d="M7 12.5l3 3 7-7" stroke="#fff" strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" className="store-header-icon" aria-hidden="true">
      <path d="M12 22s7-7.05 7-12a7 7 0 1 0-14 0c0 4.95 7 12 7 12z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <circle cx="12" cy="10" r="2.5" fill="currentColor" />
    </svg>
  );
}

function FlashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" className="store-header-icon" aria-hidden="true">
      <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" fill="currentColor" stroke="none" />
    </svg>
  );
}

function PeopleIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" className="store-header-icon" aria-hidden="true">
      <path
        d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M13 3.13a4 4 0 0 1 0 7.75M21 21v-2a4 4 0 0 0-3-3.87"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="9" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function MessageIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function couponHeadline(coupon, t) {
  if (coupon.discountType === 3) return t("freeDelivery");
  if (coupon.discountType === 1) return t("percentOff", { value: coupon.discountValue });
  return t("amountOff", { value: coupon.discountValue });
}

function daysUntil(value) {
  return Math.ceil((new Date(value).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function CouponTicket({ coupon }) {
  const { t } = useTranslation("storeHeader");
  const [copied, setCopied] = useState(false);
  const usedUp = coupon.maxUsesPerCustomer != null && coupon.customerUsedCount >= coupon.maxUsesPerCustomer;
  const daysLeft = daysUntil(coupon.expiresAt);

  function handleCopy() {
    if (usedUp) return;
    navigator.clipboard?.writeText(coupon.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className={`store-coupon-ticket ${usedUp ? "used" : ""}`}>
      <div className="store-coupon-ticket-left">
        <span className="store-coupon-headline">{couponHeadline(coupon, t)}</span>
        <span className="store-coupon-meta">
          {usedUp
            ? t("couponAlreadyUsed")
            : daysLeft <= 3
            ? t("couponEndsIn", { days: Math.max(daysLeft, 0) })
            : t("couponMinOrder", { amount: coupon.minOrderAmount })}
        </span>
      </div>
      <div className="store-coupon-ticket-notch" />
      <button type="button" className="store-coupon-ticket-right" onClick={handleCopy} disabled={usedUp}>
        <span className="store-coupon-code">{coupon.code}</span>
        <span className="store-coupon-copy">{copied ? t("copied") : t("copy")}</span>
      </button>
    </div>
  );
}

export default function StoreHeader({ store, loading }) {
  const { t } = useTranslation("storeHeader");
  const navigate = useNavigate();
  const { isAuthenticated, ensureSession, openLoginModal } = useAuth();
  const [isFollowing, setIsFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [coupons, setCoupons] = useState([]);
  const [messageBusy, setMessageBusy] = useState(false);

  useEffect(() => {
    setFollowersCount(store?.followersCount ?? 0);
  }, [store?.followersCount]);

  useEffect(() => {
    if (!store?.storeId) return;
    getStoreCoupons(store.storeId)
      .then(setCoupons)
      .catch(() => setCoupons([]));
  }, [store?.storeId]);

  useEffect(() => {
    if (!store?.storeId || !isAuthenticated) {
      setIsFollowing(false);
      return;
    }
    getStoreFollowStatus(store.storeId)
      .then(setIsFollowing)
      .catch(() => setIsFollowing(false));
  }, [store?.storeId, isAuthenticated]);

  async function handleToggleFollow() {
    let session;
    try {
      session = await ensureSession();
    } catch {
      session = null;
    }
    if (!session) {
      openLoginModal();
      return;
    }
    if (followBusy) return;
    setFollowBusy(true);
    const wasFollowing = isFollowing;
    setIsFollowing(!wasFollowing);
    setFollowersCount((count) => Math.max(0, count + (wasFollowing ? -1 : 1)));
    try {
      if (wasFollowing) await unfollowStore(store.storeId);
      else await followStore(store.storeId);
    } catch {
      setIsFollowing(wasFollowing);
      setFollowersCount((count) => Math.max(0, count + (wasFollowing ? 1 : -1)));
    } finally {
      setFollowBusy(false);
    }
  }

  async function handleMessage() {
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }
    if (messageBusy) return;
    setMessageBusy(true);
    try {
      const conversation = await startStoreConversation(store.storeId);
      navigate(`/messages?conversationId=${conversation.conversationId}`);
    } catch {
      // ignored - user can retry
    } finally {
      setMessageBusy(false);
    }
  }

  if (loading || !store) {
    return (
      <div className="store-header">
        <Skeleton className="store-header-cover-skeleton" />
        <div className="store-header-info">
          <Skeleton className="store-header-logo-skeleton" />
          <Skeleton className="store-header-name-skeleton" />
          <Skeleton className="store-header-meta-skeleton" />
        </div>
      </div>
    );
  }

  const city = store.cityNameEnglish || store.city;
  const rating = Number(store.rating ?? 0);

  return (
    <div className="store-header">
      <div className="store-header-cover-wrap">
        {store.coverImageUrl ? (
          <img src={store.coverImageUrl} alt="" className="store-header-cover-image" />
        ) : (
          <div className="store-header-cover-image store-header-cover-placeholder" />
        )}
        <div className="store-header-cover-scrim" />
      </div>

      <div className="store-header-body">
        <div className="store-header-logo-ring">
          {store.logoUrl ? (
            <img src={store.logoUrl} alt="" className="store-header-logo" />
          ) : (
            <div className="store-header-logo store-header-logo-placeholder">{store.storeName.charAt(0)}</div>
          )}
        </div>

        <div className="store-header-info">
          <div className="store-header-name-row">
            <h1 className="store-header-name">{store.storeName}</h1>
            {store.isApproved && <VerifiedIcon />}
            {store.isPrime && (
              <span className="store-header-prime-badge">
                <FlashIcon /> {t("prime")}
              </span>
            )}
          </div>

          <div className="store-header-meta-row">
            {city && (
              <span className="store-header-meta-item">
                <PinIcon />
                {city}
                {store.addressText ? ` · ${store.addressText}` : ""}
              </span>
            )}
            <span className="store-header-meta-item">
              {store.isPrime ? <FlashIcon /> : null}
              {store.isPrime ? t("primeDelivery") : t("standardDelivery")}
            </span>
          </div>

          <div className="store-header-stats-row">
            <span className="store-header-stat">
              <span className="store-header-stat-star">★</span>
              <strong>{rating.toFixed(1)}</strong> {t("ratingLabel")}
            </span>
            <span className="store-header-stat-divider" />
            <span className="store-header-stat">
              <PeopleIcon />
              <strong>{followersCount}</strong> {t("followerLabel", { count: followersCount })}
            </span>
          </div>

          <div className="store-header-actions-row">
            <button type="button" className="store-header-message-button" onClick={handleMessage} disabled={messageBusy}>
              <MessageIcon /> {t("messageButton")}
            </button>
            <button
              type="button"
              className={`store-header-follow-button ${isFollowing ? "following" : ""}`}
              onClick={handleToggleFollow}
              disabled={followBusy}
            >
              {isFollowing ? <CheckIcon /> : <PlusIcon />}
              {isFollowing ? t("following") : t("follow")}
            </button>
          </div>

          {store.description && <p className="store-header-description">{store.description}</p>}

          {coupons.length > 0 && (
            <div className="store-header-coupons">
              <div className="store-header-coupons-title">
                {t("dealsTitle")} <span className="store-header-coupons-count">{coupons.length}</span>
              </div>
              <div className="store-header-coupons-row">
                {coupons.map((coupon) => (
                  <CouponTicket coupon={coupon} key={coupon.couponId} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
